import { useEffect, useRef, useState } from 'react';
import type { DigitalMenuCategory, DigitalMenuPage, DigitalMenuProduct, DigitalMenuProductDetail, DigitalMenuProfile } from 'shared-models';
import { digitalMenuService, DigitalMenuError } from '../../services/digital-menu/digital-menu.service';
import { basketTotal, type DigitalMenuBasketLine } from './digital-menu.utils';

const emptyPage = <Item,>(): DigitalMenuPage<Item> => ({ items: [], page: 0, hasMore: false });

export function useDigitalMenuController(tenantId: string) {
  const [profile, setProfile] = useState<DigitalMenuProfile | null>(null);
  const [categories, setCategories] = useState(emptyPage<DigitalMenuCategory>);
  const [catalog, setCatalog] = useState(emptyPage<DigitalMenuProduct>);
  const [categoryId, setCategoryId] = useState<number | undefined>();
  const [search, setSearch] = useState('');
  const [effectiveSearch, setEffectiveSearch] = useState('');
  const catalogCategoryId = effectiveSearch ? undefined : categoryId;
  const [loading, setLoading] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [error, setError] = useState('');
  const [unavailable, setUnavailable] = useState(false);
  const [reload, setReload] = useState(0);
  const [detail, setDetail] = useState<{ opened: boolean; product: DigitalMenuProductDetail | null; loading: boolean; error: string }>({ opened: false, product: null, loading: false, error: '' });
  const [basket, setBasket] = useState<DigitalMenuBasketLine[]>([]);
  const [basketOpened, setBasketOpened] = useState(false);
  const listLock = useRef(false);
  const categoryLock = useRef(false);
  const unavailableRef = useRef(false);
  const generation = useRef(0);
  const listAbort = useRef<AbortController | null>(null);
  const profileAbort = useRef<AbortController | null>(null);
  const detailAbort = useRef<AbortController | null>(null);

  const failure = (reason: unknown) => {
    if (reason instanceof DigitalMenuError && [403, 404].includes(reason.status)) {
      unavailableRef.current = true;
      setUnavailable(true); setProfile(null); setCatalog(emptyPage()); setCategories(emptyPage());
      setBasket([]); setBasketOpened(false); setDetail({ opened: false, product: null, loading: false, error: '' });
    }
    setError(reason instanceof DigitalMenuError ? reason.message : 'לא ניתן לטעון את התפריט. נסו שוב.');
  };

  useEffect(() => {
    const controller = new AbortController();
    profileAbort.current = controller;
    unavailableRef.current = false;
    setUnavailable(false); setProfile(null); setCategories(emptyPage()); setError('');
    categoryLock.current = true;
    setLoadingCategories(true);
    let checking = true;
    Promise.all([digitalMenuService.profile(tenantId, controller.signal), digitalMenuService.categories(tenantId, 1, controller.signal)])
      .then(([incomingProfile, incomingCategories]) => {
        if (!controller.signal.aborted && !unavailableRef.current) { setProfile(incomingProfile); setCategories(incomingCategories); }
      }).catch((reason) => { if (!controller.signal.aborted) failure(reason); })
      .finally(() => { if (!controller.signal.aborted) { categoryLock.current = false; setLoadingCategories(false); checking = false; } });
    const revalidate = async () => {
      if (checking || controller.signal.aborted || unavailableRef.current) return;
      checking = true;
      try {
        const incoming = await digitalMenuService.profile(tenantId, controller.signal);
        if (!controller.signal.aborted && !unavailableRef.current) setProfile(incoming);
      } catch (reason) { if (!controller.signal.aborted) failure(reason); }
      finally { checking = false; }
    };
    const visible = () => { if (document.visibilityState === 'visible') void revalidate(); };
    const restored = (event: PageTransitionEvent) => { if (event.persisted) void revalidate(); };
    document.addEventListener('visibilitychange', visible);
    window.addEventListener('pageshow', restored);
    return () => {
      controller.abort(); detailAbort.current?.abort();
      document.removeEventListener('visibilitychange', visible); window.removeEventListener('pageshow', restored);
    };
    // Failure handling only writes state; it does not read mutable render snapshots.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, reload]);

  useEffect(() => {
    const timer = window.setTimeout(() => setEffectiveSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const controller = new AbortController();
    listAbort.current = controller;
    const current = ++generation.current;
    listLock.current = true; setLoading(true); setCatalog(emptyPage());
    digitalMenuService.products(tenantId, 1, catalogCategoryId, controller.signal, effectiveSearch)
      .then((page) => { if (!controller.signal.aborted && !unavailableRef.current) { setCatalog(page); setError(''); } })
      .catch((reason) => { if (!controller.signal.aborted) failure(reason); })
      .finally(() => { if (generation.current === current) { listLock.current = false; setLoading(false); } });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, catalogCategoryId, effectiveSearch, reload]);

  const loadMore = async () => {
    if (listLock.current || !catalog.hasMore || unavailableRef.current) return;
    listLock.current = true; setLoading(true);
    const current = generation.current;
    const signal = listAbort.current?.signal;
    try {
      const page = await digitalMenuService.products(tenantId, catalog.page + 1, catalogCategoryId, signal, effectiveSearch);
      if (!signal?.aborted && current === generation.current && !unavailableRef.current) {
        setCatalog((previous) => ({ ...page, items: Array.from(new Map([...previous.items, ...page.items].map((item) => [item.id, item])).values()) }));
        setError('');
      }
    } catch (reason) { if (!signal?.aborted) failure(reason); }
    finally { if (current === generation.current) { listLock.current = false; setLoading(false); } }
  };

  const loadCategories = async () => {
    if (categoryLock.current || !categories.hasMore || unavailableRef.current) return;
    categoryLock.current = true; setLoadingCategories(true);
    const signal = profileAbort.current?.signal;
    try {
      const page = await digitalMenuService.categories(tenantId, categories.page + 1, signal);
      if (!signal?.aborted && !unavailableRef.current) setCategories((previous) => ({ ...page, items: [...previous.items, ...page.items] }));
    } catch (reason) { if (!signal?.aborted) failure(reason); }
    finally { if (!signal?.aborted) { categoryLock.current = false; setLoadingCategories(false); } }
  };

  const openDish = async (id: number) => {
    if (unavailableRef.current) return;
    detailAbort.current?.abort();
    const controller = new AbortController(); detailAbort.current = controller;
    setDetail({ opened: true, product: null, loading: true, error: '' });
    try {
      const product = await digitalMenuService.product(tenantId, id, controller.signal);
      if (!controller.signal.aborted && !unavailableRef.current) setDetail({ opened: true, product, loading: false, error: '' });
    } catch (reason) {
      if (controller.signal.aborted) return;
      if (reason instanceof DigitalMenuError && reason.status === 403) { failure(reason); return; }
      setDetail({ opened: true, product: null, loading: false, error: 'המנה אינה זמינה כרגע' });
      if (reason instanceof DigitalMenuError && reason.status === 404) {
        try { await digitalMenuService.profile(tenantId, controller.signal); }
        catch (profileError) { if (!controller.signal.aborted) failure(profileError); }
      }
    }
  };

  const closeDish = () => { detailAbort.current?.abort(); setDetail({ opened: false, product: null, loading: false, error: '' }); };
  const addDish = (line: DigitalMenuBasketLine) => {
    if (unavailableRef.current) return;
    setBasket((previous) => {
      const existing = previous.find((item) => item.key === line.key);
      return existing ? previous.map((item) => item.key === line.key ? { ...item, quantity: Math.min(99, item.quantity + line.quantity) } : item) : [...previous, line];
    });
    closeDish();
  };
  const changeQuantity = (key: string, delta: number) => setBasket((previous) => previous
    .map((line) => line.key === key ? { ...line, quantity: Math.min(99, line.quantity + delta) } : line).filter((line) => line.quantity > 0));

  return { profile, categories, catalog, categoryId, setCategoryId, search, setSearch, loading, loadingCategories,
    error, unavailable, retry: () => setReload((value) => value + 1), loadMore, loadCategories, detail, openDish, closeDish,
    basket, basketOpened, setBasketOpened, addDish, changeQuantity, removeDish: (key: string) => setBasket((previous) => previous.filter((line) => line.key !== key)),
    total: basketTotal(basket), count: basket.reduce((sum, line) => sum + line.quantity, 0) };
}